import { EMPTY_SIGNUP_FORM, SignupForm } from "./signupForm";

import { PROVIDER_SUPPORT_PHONE_NUMBER } from "../../constants";

const SUPPORT = `Need help? Call us at ${PROVIDER_SUPPORT_PHONE_NUMBER}.`;

/** Server errors for form fields, keyed like the form. */
export type FieldErrors = Partial<Record<keyof SignupForm, string>>;

export type SubmitResult =
  | { ok: true }
  | { fieldErrors: FieldErrors }
  | { error: string };

const RETRY_MESSAGE = `We couldn't create your account right now. Please try again. ${SUPPORT}`;

/** "first_name" → "firstName", to key the backend's field errors like the form. */
const toFormField = (field: string) =>
  field.replace(/_([a-z])/g, (_, letter: string) =>
    letter.toUpperCase(),
  ) as keyof SignupForm;

/** Posts the sign-up. Resolves to success, errors on specific fields, or a
 * message for the whole form. Never logs the request: it has the password. */
export const submitSignup = async (form: SignupForm): Promise<SubmitResult> => {
  try {
    const response = await fetch("/graphql/provider-signup", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        npi: form.npi,
        npi_attested: form.npiAttested,
        email: form.email,
        password: form.password,
        first_name: form.firstName,
        last_name: form.lastName,
        phone: form.phone,
        facility_name: form.facilityName,
        facility_type_id: form.facilityTypeId,
        address1: form.address1,
        address2: form.address2,
        city: form.city,
        state_abbr: form.stateAbbr,
        zipcode: form.zipcode,
        facility_phone: form.facilityPhone,
        facility_fax: form.facilityFax,
        secondary_facility_fax: form.secondaryFacilityFax,
        medical_records_fax: form.medicalRecordsFax,
      }),
    });
    if (response.status >= 500) {
      return { error: RETRY_MESSAGE };
    }
    const body = await response.json();
    if (body.ok) {
      return { ok: true };
    }
    if (Array.isArray(body.errors) && body.errors.length > 0) {
      const fieldErrors: FieldErrors = {};
      for (const { field, message } of body.errors) {
        const formField = toFormField(field);
        // A field the form doesn't have can't be shown, so it fails the form.
        if (!(formField in EMPTY_SIGNUP_FORM)) {
          return { error: `We couldn't create your account. ${SUPPORT}` };
        }
        // Validation messages arrive as "Value error, Enter a valid NPI".
        fieldErrors[formField] = String(message).replace(/^Value error, /, "");
      }
      return { fieldErrors };
    }
    return {
      error: `${body.error ?? "We couldn't create your account."} ${SUPPORT}`,
    };
  } catch {
    return { error: RETRY_MESSAGE };
  }
};
